import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import "./Empaquetado.css";
import { useLocation } from "react-router";
import { formatIDArt } from "@/ts/util";
import { useCallback, useEffect, useRef, useState } from "react";
import { Loading, QueryError } from "@/components/Loading/Loading";
import { Breadcrumb } from "@/components/Breadcrumb";
import { toast } from "react-toastify";

export function EmpaquetadoPage() {
	const queryClient = useQueryClient();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const idPedido = params.get("idPedido");
	if (idPedido == null) throw new Error("No se ha especificado un idPedido en la URL");
	const { data, error, isPending } = useSavedEstructura(idPedido);

	const [dirtyEstructura, setDirtyEstructura] = useState(data);
	useEffect(() => {
		console.log("Loaded estructura:", data);
		setDirtyEstructura(data);
	}, [data]);

	const onChildUpdate = useCallback((dropped: ItemEstructura, droppedFrom: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }, droppedInto: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }) => {
		if (dirtyEstructura == null) return;

		//#region Validaciones
		if (dropped.tipo == "articulo" && droppedInto.tipo == "top") {
			toast("Los articulos no pueden estar sueltos en el albaran, deben ir dentro de un bulto o pallet.", { type: "warning" });
			return;
		}
		if (droppedFrom.tipo == droppedInto.tipo && droppedFrom.id === droppedInto.id) {
			console.log("Dropped into the same container, no changes made.");
			return;
		}
		if (dropped.tipo == "pallet" && droppedInto.id != null) {
			toast("Los pallets no pueden ir dentro de otros elementos.", { type: "warning" });
			return;
		}
		if (dropped.tipo == droppedInto.tipo && dropped.id === droppedInto.id) {
			// Lo hemos soltado sobre nosotros mismos, me suena a un cancelado
			return;
		}
		//#endregion

		const newEstructura = { ...dirtyEstructura };

		if (droppedFrom.id == null) {
			newEstructura.estructura = newEstructura.estructura.filter(item => item.id !== dropped.id || item.tipo !== dropped.tipo);
		} else {
			const previousParent = findItemEstructura(droppedFrom.id, droppedFrom.tipo, newEstructura.estructura);
			if (previousParent) {
				previousParent.contenido = previousParent.contenido.filter(item => item.id !== dropped.id || item.tipo !== dropped.tipo);
			}
		}

		if (droppedInto.id == null) {
			newEstructura.estructura = [...newEstructura.estructura, dropped];
		} else {
			const newParent = findItemEstructura(droppedInto.id, droppedInto.tipo, newEstructura.estructura);
			if (newParent) {
				newParent.contenido = [...newParent.contenido, dropped];
			}
		}

		setDirtyEstructura(newEstructura);
	}, [dirtyEstructura]);


	if (error) return <QueryError error={error} fallback="Error al cargar la estructura guardada" />;

	const guardarEstructura = () => {
		if (dirtyEstructura == null) {
			toast("No hay estructura que guardar.", { type: "warning" });
			return;
		}
		const props = {
			idEstructura: dirtyEstructura.idEstructura,
			contenido: dirtyEstructura.estructura
		};
		void sendAjaxRequest<RespuestaGuardarEstructura>("POST", "/api/v1/guardarEstructura", props)
			.then((response) => {
				toast(response.message, { type: "info" });

				return queryClient.invalidateQueries({ queryKey: ["getEstructuraPedidoVenta", idPedido] });
			});
	};

	return (
		<div style={{ display: "flex", flex: 1 }}>
			<div id="contenedor">
				<Breadcrumb
					sectionTitle="Pedidos Venta"
					links={[
						{ label: "Pedidos Venta", to: "/cycles" },
						{ label: `Empaquetado ${idPedido}`, active: true },
					]}
				/>

				<div id="albaran" className="list">

					{isPending ? <Loading /> : <AlbaranPaquete
						dirtyEstructura={dirtyEstructura}
						onChildUpdate={onChildUpdate}
					/>}

				</div>

				<button className="btnPrimary" onClick={guardarEstructura} type="button">
					Guardar Estructura
				</button>
			</div>
			<SidebarPorUbicar estructura={dirtyEstructura} onChildUpdate={onChildUpdate} />
		</div>
	);
}




function AlbaranPaquete(props: { readonly dirtyEstructura: Estructura | undefined; readonly onChildUpdate: OnChildUpdate; }) {
	const { onChildUpdate } = props;
	const palletsEnAlbaran = props.dirtyEstructura?.estructura.filter(item => item.tipo === "pallet") ?? [];
	const bultosEnAlbaran = props.dirtyEstructura?.estructura.filter(item => item.tipo === "bulto") ?? [];

	const onNewContent = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		const movementData = JSON.parse(event.dataTransfer.getData("application/json")) as EventData;
		onChildUpdate(movementData.dropped, movementData.droppedFrom, { tipo: "top" });
	}, [onChildUpdate]);

	const listener = useCallback((event: React.DragEvent) => { event.preventDefault(); }, []);

	return (
		<div
			id="albaranContainer"
			onDrop={onNewContent}
			onDragOver={listener}
		>
			{palletsEnAlbaran.map((pallet) => <ItemPallet key={pallet.codigo} data={pallet} parent={{ tipo: "top" }} onChange={onChildUpdate} />)}
			{bultosEnAlbaran.map((bulto) => <ItemBulto key={bulto.codigo} data={bulto} parent={{ tipo: "top" }} onChange={onChildUpdate} />)}
		</div>
	);
}


interface ItemArticuloProps<T> {
	readonly data: T;
	readonly parent: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; };
}
interface ItemContenedorProps<T> extends ItemArticuloProps<T> {
	readonly onChange: OnChildUpdate;
}
function ItemArticulo(props: ItemArticuloProps<ItemEstructuraArt>) {
	const { data, parent } = props;
	const onStartMove = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.dataTransfer.setData("application/json", JSON.stringify({
			dropped: data,
			droppedFrom: parent,
		}));
		event.dataTransfer.effectAllowed = "move";
	}, [data, parent]);

	return (
		<div className="list-item articulo" draggable onDragStart={onStartMove}>
			<div>{formatIDArt(data.idArticulo, 0)}</div>
			<div>{data.descripcion}</div>
			<div>{data.cantidad} unidades</div>
		</div>
	);
}
function ItemBulto(props: ItemContenedorProps<ItemEstructuraBul>) {
	const { data, parent, onChange } = props;
	const contenido = props.data.contenido;
	const bultosInside = contenido.filter(item => item.tipo === "bulto");
	const articulosInside = contenido.filter(item => item.tipo === "articulo");

	const onStartMove = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.dataTransfer.setData("application/json", JSON.stringify({
			dropped: data,
			droppedFrom: parent,
		} satisfies EventData));
		event.dataTransfer.effectAllowed = "move";
	}, [data, parent]);
	const onNewContent = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		const movementData = JSON.parse(event.dataTransfer.getData("application/json")) as EventData;
		onChange(movementData.dropped, movementData.droppedFrom, data);
	}, [data, onChange]);

	return (
		<div className="list bulto" draggable onDragStart={onStartMove} onDrop={onNewContent}>
			<div className="headerContenedor">
				<h3>Bulto</h3>
				{/* <input type="number" className="inputEstructura" value={props.data.codigo} onChange={(e) => props.onChange({ ...props.data, codigo: Number(e.target.value) })} /> */}
			</div>
			{articulosInside.map((art) => <ItemArticulo key={art.idLineaPedido} data={art} parent={props.data} />)}
			{bultosInside.map((bul) => <ItemBulto key={bul.codigo} data={bul} parent={props.data} onChange={props.onChange} />)}
		</div>
	);
}
function ItemPallet(props: ItemContenedorProps<ItemEstructuraPallet>) {
	const { data, parent, onChange } = props;
	const contenido = data.contenido;
	const bultosInside = contenido.filter(item => item.tipo === "bulto");
	const articulosInside = contenido.filter(item => item.tipo === "articulo");

	const onStartMove = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.dataTransfer.setData("application/json", JSON.stringify({
			dropped: data,
			droppedFrom: parent,
		} satisfies EventData));
		event.dataTransfer.effectAllowed = "move";
	}, [data, parent]);
	const onNewContent = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		const movementData = JSON.parse(event.dataTransfer.getData("application/json")) as EventData;
		onChange(movementData.dropped, movementData.droppedFrom, data);
	}, [data, onChange]);

	return (
		<div className="list pallet" draggable="true" onDragStart={onStartMove} onDrop={onNewContent}>
			<div className="headerContenedor">
				<h3>Pallet</h3>
				{/* <input type="number" className="inputEstructura" value={props.data.codigo} onChange={(e) => props.onChange({ ...props.data, codigo: Number(e.target.value) })} /> */}
			</div>
			{articulosInside.map((art) => <ItemArticulo key={art.idLineaPedido} data={art} parent={props.data} />)}
			{bultosInside.map((bul) => <ItemBulto key={bul.codigo} data={bul} parent={props.data} onChange={props.onChange} />)}
		</div>
	);
}


function SidebarPorUbicar(props: { readonly estructura: Estructura | undefined; readonly onChildUpdate: OnChildUpdate; }) {
	const { onChildUpdate } = props;
	const contenido = props.estructura?.estructura ?? [];

	const articulosSueltos = contenido.filter(item => item.tipo === "articulo");

	const onNewContent = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		const movementData = JSON.parse(event.dataTransfer.getData("application/json")) as EventData;
		onChildUpdate(movementData.dropped, movementData.droppedFrom, { tipo: "sinUbicar" });
	}, [onChildUpdate]);

	const listener = useCallback((event: React.DragEvent) => { event.preventDefault(); }, []);


	return (
		<div
			className="sidebar"
			style={{ "overflowY": "auto", "overflowX": "hidden" }}
			onDrop={onNewContent}
			onDragOver={listener}
		>
			<h3>Articulos</h3>
			<div id="articulosSinUbicar">
				{articulosSueltos.map(art => <ItemArticulo key={art.idLineaPedido} data={art} parent={{ tipo: "sinUbicar" }} />)}
			</div>

			<h3>Acciones</h3>
			<div id="acciones">
				<NewContenedor tipo="bulto" texto="Nuevo bulto" />
				<NewContenedor tipo="pallet" texto="Nuevo pallet" />
			</div>
		</div>
	);
}
function NewContenedor(props: { readonly tipo: "bulto" | "pallet"; readonly texto: string; }) {
	const lastId = useRef(-1); // TODO: Obtener el último ID usado

	const onStartMove = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.dataTransfer.setData("application/json", JSON.stringify({
			dropped: {
				tipo: props.tipo,
				id: lastId.current--,	// Esto creara un ID negativo cada vez que arrastres, aunque no completes. No me preocupa demasiado
				codigo: 0,
				contenido: [],
			},
			droppedFrom: { tipo: "sinUbicar" },
		} satisfies EventData));
		event.dataTransfer.effectAllowed = "move";
	}, [props.tipo]);

	return (
		<div className={`list-item ${props.tipo}`} draggable="true" onDragStart={onStartMove}>
			<h3>
				{props.texto}
			</h3>
		</div>
	);
}


function useSavedEstructura(idPedidoVenta: string) {
	return useQuery({
		queryKey: ["getEstructuraPedidoVenta", idPedidoVenta],
		queryFn: () => sendAjaxRequest<Estructura>("POST", "/api/v1/getEstructuraPedidoVenta", { idPedidoVenta }).then(response => {
			assignIds(response.estructura, -1);
			return response;
		}),
	});
}
interface Estructura {
	idEstructura: number,
	estructura: ItemEstructura[];
}
type ItemEstructura = ItemEstructuraArt | ItemEstructuraBul | ItemEstructuraPallet;
interface ItemEstructuraArt {
	tipo: "articulo";
	id: number;

	idPedido: string;
	idLineaPedido: string;

	idArticulo: string;
	descripcion: string;
	cantidad: number;
	cantidadEmpaquetada: number;
}
interface ItemEstructuraBul {
	tipo: "bulto";
	id: number;

	codigo: number;
	contenido: ItemEstructura[];
}
interface ItemEstructuraPallet {
	tipo: "pallet";
	id: number;

	codigo: number;
	contenido: ItemEstructura[];
}


interface RespuestaGuardarEstructura {
	message: string;
}

function findItemEstructura(id: number | string, tipo: "bulto" | "pallet", estructura: ItemEstructura[]): ItemEstructuraBul | ItemEstructuraPallet | null {
	for (const item of estructura) {
		if (item.id === id && item.tipo === tipo) return item;
		if (item.tipo === "bulto" || item.tipo === "pallet") {
			const found = findItemEstructura(id, tipo, item.contenido);
			if (found) return found;
		}
	}
	return null;
}
function assignIds(estructura: ItemEstructura[], startFrom: number) {
	let currentId = startFrom;
	for (const item of estructura) {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Es para lo que usamos la función xd
		if (item.id != undefined && item.id > 0) continue; // Ya tiene ID asignado

		item.id = currentId--;
		if (item.tipo === "bulto" || item.tipo === "pallet") {
			assignIds(item.contenido, currentId);
		}
	}
}
interface EventData {
	dropped: ItemEstructura;
	droppedFrom: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; };
}

type OnChildUpdate = (dropped: ItemEstructura, droppedFrom: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }, droppedInto: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }) => void;